import { Stepper } from '@avanta/shared/components/Stepper';
import { Button } from '@avanta/shared/components/ui/Button';
import { LOCIZE_NS } from '@avanta/shared/constants/common';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router';
import { chatReportSteps, StepComponentTypes } from 'constants/chat-report';
import { useUserStore } from 'stores/user';
import { getUserId } from 'utils/storage';
import { Step } from './Step';
import { ISSUES, LAST_STEP_ISSUES, steps } from './steps';
import { type ChatReportFormData } from './types';
import { useSendReport } from './useSendReport';

export const ChatReport = (): React.ReactElement | null => {
  const [searchParams] = useSearchParams();
  const name = searchParams.get('name');
  const patientId = searchParams.get('patientId');
  const user = useUserStore(state => state.user);
  const [currentStep, setCurrentStep] = useState(1);
  const methods = useForm<ChatReportFormData>();
  const { handleSubmit, watch } = methods;
  const { mutate: sendReport } = useSendReport();
  const { t } = useTranslation(LOCIZE_NS.CHAT);

  useEffect(() => {
    if (!name || !patientId) {
      window.close();
    }
  }, [name, patientId]);

  const onSubmit = async (data: ChatReportFormData): Promise<void> => {
    const response = {
      supporter_id: getUserId(),
      patient_id: patientId ?? '',
      reportDTO: {
        id: null,
        issueType: data.issueType ?? ISSUES.THERAPY_SUPPORT,
        etiologyProblem: data.etiologyProblem || null,
        disorders: data.disorders || [],
        reportObservations: data.reportObservations,
      },
    };
    sendReport(response);
  };

  const handleOnCancel = (): void => {
    window.close();
  };

  if (!user) return null;

  const step = user?.type && steps[user.type as keyof typeof steps];

  const handleOnChangeStep = (newStep: number = currentStep + 1, option?: string): void => {
    const goToLastStep = LAST_STEP_ISSUES.includes(option as (typeof LAST_STEP_ISSUES)[number]);
    if (goToLastStep) {
      setCurrentStep(step.length);
      return;
    }
    setCurrentStep(newStep + 1);
  };

  const handleOnBack = (): void => {
    const issueType = watch(chatReportSteps.issueType);
    const goToLastStep = LAST_STEP_ISSUES.includes(issueType as (typeof LAST_STEP_ISSUES)[number]);
    if (goToLastStep) {
      setCurrentStep(step.findIndex(step => step.name === chatReportSteps.issueType) + 1);
      return;
    }
    setCurrentStep(currentStep - 1);
  };

  const isValidMultipleStep = (currentStep: number): boolean => {
    const stepName = step[currentStep - 1].name;
    const value = watch(stepName as keyof ChatReportFormData);
    return Array.isArray(value) ? value.length > 0 : !!value;
  };

  return (
    <div className="flex flex-col gap-4 items-center w-full p-4 py-12">
      <div className="w-full max-w-7xl flex gap-4 justify-between max-am-lg:flex-col-reverse max-am-lg:items-start">
        <div className="flex flex-col gap-2">
          <h2 className="text-2xl font-semibold">{t('ChatReport.heading', { name })}</h2>
          <h4>
            {t('ChatReport.date.label')} {new Date().toLocaleDateString('es-ES')}
          </h4>
        </div>
        <Button variant="terciary" onClick={handleOnCancel}>
          {t('Common.cancel', { ns: LOCIZE_NS.COMMON })}
        </Button>
      </div>
      <div className="flex flex-col gap-8 w-full max-w-md mx-auto">
        <Stepper
          steps={step}
          currentStep={currentStep}
          i18nKey="ChatReport"
          i18nNs={LOCIZE_NS.CHAT}
          onMobileBack={currentStep > 1 ? handleOnBack : undefined}
        />
        <FormProvider {...methods}>
          <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-6">
            {currentStep > 1 && (
              <div className="flex items-center justify-between">
                <Button
                  type="button"
                  variant="terciary"
                  onClick={handleOnBack}
                  className="flex items-center gap-2 p-0"
                  icon="leading"
                >
                  <ArrowLeft className="w-4 h-4" />
                  {t('Common.back', { ns: LOCIZE_NS.COMMON })}
                </Button>
                {step[currentStep - 1].component === StepComponentTypes.checkbox && (
                  <Button
                    type="button"
                    onClick={() => setCurrentStep(currentStep + 1)}
                    disabled={!isValidMultipleStep(currentStep)}
                    className="flex items-center"
                    icon="leading"
                  >
                    {t('Common.next', { ns: LOCIZE_NS.COMMON })}
                    <ArrowRight className="w-4 h-4" />
                  </Button>
                )}
              </div>
            )}
            <Step step={step[currentStep - 1]} onNext={handleOnChangeStep} />
            {currentStep === step.length && (
              <Button type="submit" className="w-full mt-4">
                {t('ChatReport.cta')}
              </Button>
            )}
          </form>
        </FormProvider>
      </div>
    </div>
  );
};
